import axios, { CancelTokenSource } from 'axios';
import { useSnackbar } from 'notistack5';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  IdIssuanceIssueFormInputs,
  IdIssuanceVerifyFormInputs,
  PaginatedResponse,
  PendingRespondent,
  PendingRespondentCreateResponseDto,
  SearchParams,
  SingleResponse,
} from 'src/@types/pendingRespondentStatus';
import { apiGateway } from 'src/config';
import { useBearerToken } from './useBearerToken';

export const usePendingRespondentApi = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { getBearerToken } = useBearerToken();

  const postPendingRespondent = async (respondentRequestId: string, emails: string[]): Promise<PendingRespondentCreateResponseDto | undefined> => {
    try {
      const token = await getBearerToken();
      if (!token) return;

      const response = await axios.post<PendingRespondentCreateResponseDto>(
        `${apiGateway}/pending-respondent/${respondentRequestId}`,
        { emails },
        {
          headers: { Authorization: token },
          timeout: 60000,
        },
      );
      return response.data;
    } catch (error) {
      enqueueSnackbar(t('apiExceptionMessage.usePendingRespondentApi.failedPostData'), { variant: 'error' });
      throw error;
    }
  };

  const deletePendingRespondent = async (respondentRequestId: string, emails: string[]): Promise<boolean> => {
    try {
      const token = await getBearerToken();
      if (!token) return false;

      await axios.post(
        `${apiGateway}/pending-respondent/delete/${respondentRequestId}`,
        { emails },
        {
          headers: { Authorization: token },
          timeout: 60000,
        },
      );
      enqueueSnackbar(t('message.deleted'), { variant: 'success' });
      return true;
    } catch (error) {
      enqueueSnackbar(t('apiExceptionMessage.usePendingRespondentApi.failedDeleteData'), { variant: 'error' });
      return false;
    }
  };

  const agree = async (formData: IdIssuanceVerifyFormInputs, token: string) => {
    try {
      await axios.put<{ success: boolean }>(
        `${apiGateway}/pending-respondent/agree`,
        {
          ...formData,
          token,
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
          timeout: 60000,
        },
      );
      enqueueSnackbar(t('message.register'), { variant: 'success' });
      navigate('/auth/login');
    } catch (error) {
      enqueueSnackbar(t('apiExceptionMessage.usePendingRespondentApi.failedRegisterData'), { variant: 'error' });
    }
  };

  const getPendingRespondents = async (searchParams: SearchParams) => {
    const params = new URLSearchParams({
      searchText: searchParams.searchText,
      status: searchParams.status,
      sortModel: JSON.stringify(searchParams.sortModel),
      deletable: JSON.stringify(searchParams.deletable),
      page: searchParams.currentPage ? searchParams.currentPage.toString() : '1',
      pageSize: searchParams.pageSize.toString(),
    });

    const cancelToken: CancelTokenSource = axios.CancelToken.source();

    try {
      const token = await getBearerToken();
      if (!token) return;
      const response = await axios.get<PaginatedResponse<PendingRespondent>>(`${apiGateway}/pending-respondent/list`, {
        headers: { Authorization: token },
        params,
        cancelToken: cancelToken.token,
        timeout: 60000,
      });
      return response.data;
    } catch (error) {
      if (!axios.isCancel(error)) {
        enqueueSnackbar(t('apiExceptionMessage.usePendingRespondentApi.failedGetList'), { variant: 'error', preventDuplicate: true });
      }
      throw error;
    }
  };

  const getPendingRespondentById = async (id: string) => {
    try {
      const token = await getBearerToken();
      if (!token) return;
      const response = await axios.get<SingleResponse<PendingRespondent>>(`${apiGateway}/pending-respondent/${id}`, {
        headers: { Authorization: token },
        timeout: 60000,
      });
      return response.data;
    } catch (error) {
      enqueueSnackbar(t('apiExceptionMessage.usePendingRespondentApi.failedGetData'), { variant: 'error' });
    }
  };

  const issue = async (id: string, formData: IdIssuanceIssueFormInputs) => {
    try {
      const token = await getBearerToken();
      if (!token) return;
      await axios.post(
        `${apiGateway}/pending-respondent/issue/${id}`,
        { ...formData },
        {
          headers: { Authorization: token },
          timeout: 60000,
        },
      );
      enqueueSnackbar(t('message.register'), { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(t('apiExceptionMessage.usePendingRespondentApi.failedRegisterData'), { variant: 'error' });
      throw error;
    }
  };

  return { postPendingRespondent, deletePendingRespondent, agree, getPendingRespondents, getPendingRespondentById, issue };
};
